import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface ImpersonationContextValue {
  impersonatedUserId: string | null;
  impersonatedUserName: string | null;
  impersonatedUserRole: string | null;
  isImpersonating: boolean;
  startImpersonation: (userId: string) => Promise<void>;
  stopImpersonation: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextValue>({
  impersonatedUserId: null,
  impersonatedUserName: null,
  impersonatedUserRole: null,
  isImpersonating: false,
  startImpersonation: async () => {},
  stopImpersonation: () => {},
});

export function useImpersonation() {
  return useContext(ImpersonationContext);
}

/**
 * Check admin status directly from DB (not from hooks that will be overridden).
 * Uses a ref to cache the result so we only check once per session.
 */
function useIsRealAdmin() {
  const { user: authUser } = useAuth();
  const cachedRef = useRef<boolean | null>(null);

  const checkAdmin = useCallback(async (): Promise<boolean> => {
    if (cachedRef.current !== null) return cachedRef.current;
    if (!authUser?.id) return false;

    const { data } = await supabase
      .from('app_users')
      .select('system_role:system_roles(code)')
      .eq('auth_user_id', authUser.id)
      .maybeSingle();

    const isAdmin = data?.system_role?.code === 'admin' || data?.system_role?.code === 'it';
    cachedRef.current = isAdmin;
    return isAdmin;
  }, [authUser?.id]);

  return checkAdmin;
}

export function ImpersonationProvider({ children }: { children: React.ReactNode }) {
  const [impersonatedUserId, setImpersonatedUserId] = useState<string | null>(null);
  const [impersonatedUserName, setImpersonatedUserName] = useState<string | null>(null);
  const [impersonatedUserRole, setImpersonatedUserRole] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const checkAdmin = useIsRealAdmin();

  const startImpersonation = useCallback(async (userId: string) => {
    const isAdmin = await checkAdmin();
    if (!isAdmin) {
      console.error('[Impersonation] Only admins can impersonate users');
      return;
    }

    // Fetch target user info for the banner
    const { data } = await supabase
      .from('app_users')
      .select('display_name, first_name, last_name, system_role:system_roles(name)')
      .eq('id', userId)
      .maybeSingle();

    const name = data?.display_name || [data?.first_name, data?.last_name].filter(Boolean).join(' ') || 'Usuário';
    const role = data?.system_role?.name || '';

    setImpersonatedUserId(userId);
    setImpersonatedUserName(name);
    setImpersonatedUserRole(role);

    // Invalidate queries so hooks re-fetch with impersonated user
    queryClient.invalidateQueries({ queryKey: ['current-app-user'] });
    queryClient.invalidateQueries({ queryKey: ['user-permissions'] });
  }, [checkAdmin, queryClient]);

  const stopImpersonation = useCallback(() => {
    setImpersonatedUserId(null);
    setImpersonatedUserName(null);
    setImpersonatedUserRole(null);

    queryClient.invalidateQueries({ queryKey: ['current-app-user'] });
    queryClient.invalidateQueries({ queryKey: ['user-permissions'] });
  }, [queryClient]);

  return (
    <ImpersonationContext.Provider
      value={{
        impersonatedUserId,
        impersonatedUserName,
        impersonatedUserRole,
        isImpersonating: !!impersonatedUserId,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}
